import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';

/**
 * 파일을 Firebase Storage에 업로드합니다.
 * @param {File} file - 업로드할 파일
 * @param {string} path - Storage 경로
 * @param {Function} onProgress - 업로드 진행률 콜백 (0-100)
 * @returns {Promise<{url: string, name: string, size: number}>}
 */
export const uploadFile = async (file, path, onProgress) => {
  try {
    const storageRef = ref(storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) {
            onProgress(progress);
          }
        },
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve({
              url: downloadURL,
              name: file.name,
              size: file.size
            });
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * 프로그램 참고자료 이미지 업로드
 * @param {File} imageFile - 이미지 파일
 * @param {string} programId - 프로그램 ID
 * @param {number} index - 이미지 순서
 * @param {Function} onProgress - 진행률 콜백
 * @returns {Promise<{url: string, name: string, uploadedAt: Date}>}
 */
export const uploadProgramImage = async (imageFile, programId, index, onProgress) => {
  const timestamp = Date.now();
  const path = `programs/${programId}/images/${index}_${timestamp}_${imageFile.name}`;
  const result = await uploadFile(imageFile, path, onProgress);

  return {
    url: result.url,
    name: result.name,
    uploadedAt: new Date()
  };
};

/**
 * 프로그램 참고자료 동영상 업로드
 * @param {File} videoFile - 동영상 파일
 * @param {string} programId - 프로그램 ID
 * @param {number} index - 동영상 순서
 * @param {Function} onProgress - 진행률 콜백
 * @returns {Promise<{url: string, name: string, size: number, uploadedAt: Date}>}
 */
export const uploadProgramVideo = async (videoFile, programId, index, onProgress) => {
  const timestamp = Date.now();
  const path = `programs/${programId}/videos/${index}_${timestamp}_${videoFile.name}`;
  const result = await uploadFile(videoFile, path, onProgress);

  return {
    url: result.url,
    name: result.name,
    size: result.size,
    uploadedAt: new Date()
  };
};

/**
 * Storage에서 파일 삭제
 * @param {string} fileUrl - 삭제할 파일의 URL
 * @returns {Promise<void>}
 */
export const deleteFile = async (fileUrl) => {
  try {
    const fileRef = ref(storage, fileUrl);
    await deleteObject(fileRef);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

/**
 * 파일 타입 검증
 * @param {File} file - 검증할 파일
 * @param {string} type - 'image' 또는 'video'
 * @returns {boolean}
 */
export const validateFileType = (file, type) => {
  if (type === 'image') {
    return file.type.startsWith('image/');
  } else if (type === 'video') {
    return file.type.startsWith('video/');
  }
  return false;
};

/**
 * 파일 크기 검증 (MB 단위)
 * @param {File} file - 검증할 파일
 * @param {number} maxSizeMB - 최대 크기 (MB)
 * @returns {boolean}
 */
export const validateFileSize = (file, maxSizeMB) => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

export const storageService = {
  uploadFile,
  uploadProgramImage,
  uploadProgramVideo,
  deleteFile,
  validateFileType,
  validateFileSize
};
